import { type ReactNode, useEffect, useLayoutEffect, useRef } from "react";

const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Shared by the case, material and enterprise detail galleries.
export function usePreviewFade(source: string | null) {
  const ref = useRef<HTMLDivElement>(null);
  const displayed = useRef<string | null>(null);
  useLayoutEffect(() => {
    const button = ref.current?.querySelector<HTMLElement>(".detail-preview");
    const incoming = button?.querySelector<HTMLImageElement>("img");
    if (!button || !incoming || !source) { displayed.current = null; return; }
    let stopped = false;
    let animation: Animation | undefined;
    let overlay: HTMLImageElement | undefined;
    if (displayed.current && displayed.current !== source && !reduced()) {
      overlay = document.createElement("img");
      overlay.src = displayed.current;
      overlay.alt = "";
      overlay.draggable = false;
      overlay.setAttribute("aria-hidden", "true");
      overlay.className = "detail-preview-fade";
      button.append(overlay);
    }
    const remove = () => { animation?.cancel(); overlay?.remove(); };
    const failed = () => { displayed.current = null; remove(); };
    const loaded = async () => {
      try { await incoming.decode(); } catch { if (!stopped) failed(); return; }
      if (stopped) return;
      displayed.current = source;
      if (!overlay) return;
      if (reduced()) { remove(); return; }
      animation = overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: "ease-out" });
      animation.onfinish = remove;
    };
    incoming.addEventListener("load", loaded);
    incoming.addEventListener("error", failed);
    const preference = matchMedia("(prefers-reduced-motion: reduce)");
    preference.addEventListener("change", remove);
    if (incoming.complete && incoming.naturalWidth) void loaded();
    const timeout = window.setTimeout(remove, 5000);
    return () => { stopped = true; clearTimeout(timeout); remove(); incoming.removeEventListener("load", loaded); incoming.removeEventListener("error", failed); preference.removeEventListener("change", remove); };
  }, [source]);
  return ref;
}

export function useArchiveDelight() {
  useEffect(() => {
    const move = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || reduced()) return;
      const target = (event.target as Element).closest<HTMLElement>(".project-card, .folio-action");
      if (!target) return;
      const rect = target.getBoundingClientRect();
      target.style.setProperty("--pointer-x", `${event.clientX - rect.left}px`);
      target.style.setProperty("--pointer-y", `${event.clientY - rect.top}px`);
    };
    document.addEventListener("pointermove", move, { passive: true });
    return () => { document.removeEventListener("pointermove", move); };
  }, []);
}

export function useCardReflow(content: unknown, loading: boolean) {
  const ref = useRef<HTMLElement>(null);
  const previous = useRef(new Map<string, { x: number; y: number }>());
  useLayoutEffect(() => {
    const grid = ref.current;
    if (loading || !grid) return;
    const cards = Array.from(grid.querySelectorAll<HTMLElement>("[data-motion-id]"));
    const positions = cards.map(card => ({ card, id: card.dataset.motionId!, x: card.offsetLeft, y: card.offsetTop }));
    const animations: Animation[] = [];
    for (const { card, id, x, y } of positions) {
      const old = previous.current.get(id);
      if (reduced()) continue;
      const dx = old ? old.x - x : 0;
      const dy = old ? old.y - y : 12;
      if (!old || dx || dy) animations.push(card.animate([
        { transform: `translate(${dx}px, ${dy}px)`, opacity: old ? 1 : 0 },
        { transform: "translate(0, 0)", opacity: 1 },
      ], { duration: old ? 330 : 240, easing: "cubic-bezier(.22,1,.36,1)" }));
    }
    previous.current = new Map(positions.map(({ id, x, y }) => [id, { x, y }]));
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const cancel = () => animations.forEach(animation => animation.cancel());
    media.addEventListener("change", cancel);
    return () => { cancel(); media.removeEventListener("change", cancel); };
  }, [content, loading]);
  return ref;
}

export function MotionNavigation({ active, children }: { active: string; children: ReactNode }) {
  const nav = useRef<HTMLElement>(null);
  const marker = useRef<HTMLSpanElement>(null);
  const previous = useRef<number | null>(null);
  useLayoutEffect(() => {
    const root = nav.current;
    const bar = marker.current;
    if (!root || !bar) return;
    let animation: Animation | undefined;
    const update = (animate: boolean) => {
      const selected = root.querySelector<HTMLElement>("[aria-current=page]");
      if (!selected || !selected.offsetHeight) { bar.style.opacity = "0"; previous.current = null; return; }
      const top = selected.offsetTop;
      animation?.cancel();
      bar.style.height = `${selected.offsetHeight}px`;
      bar.style.transform = `translateY(${top}px)`;
      bar.style.opacity = "1";
      if (animate && previous.current !== null && !reduced()) {
        animation = bar.animate([{ transform: `translateY(${previous.current}px)` }, { transform: `translateY(${top}px)` }],
          { duration: 300, easing: "cubic-bezier(.22,1,.36,1)" });
      }
      previous.current = top;
    };
    update(true);
    let dimensions = `${root.clientWidth}:${root.clientHeight}`;
    const observer = new ResizeObserver(() => {
      const next = `${root.clientWidth}:${root.clientHeight}`;
      if (next !== dimensions) { dimensions = next; update(false); }
    });
    observer.observe(root);
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const cancel = () => animation?.cancel();
    media.addEventListener("change", cancel);
    return () => { observer.disconnect(); animation?.cancel(); media.removeEventListener("change", cancel); };
  }, [active]);
  return <nav ref={nav} className="motion-navigation" aria-label="主导航"><span ref={marker} className="navigation-marker" aria-hidden="true" />{children}</nav>;
}
